import { addMonitorBreadcrumb } from "./monitor-breadcrumb"
import { scrubUrl, scrubValue } from "./sentry-privacy"

export interface BackendCallRecord {
  feature: string
  step: string
  endpoint: string
  method?: string
  status?: number
  statusText?: string
  durationMs?: number
  request?: unknown
  response?: unknown
  flow?: string
}

function resolveLevel(status?: number) {
  if (status == null) return "info" as const
  if (status >= 500) return "error" as const
  if (status >= 400) return "warning" as const
  return "info" as const
}

function buildPayload(record: BackendCallRecord) {
  return scrubValue({
    feature: record.feature,
    step: record.step,
    endpoint: scrubUrl(record.endpoint),
    method: record.method ?? "POST",
    status: record.status,
    statusText: record.statusText,
    duration_ms: record.durationMs,
    flow: record.flow,
    request: record.request,
    response: record.response,
  }) as Record<string, unknown>
}

export function recordBackendResponse(record: BackendCallRecord) {
  addMonitorBreadcrumb({
    category: "http",
    level: resolveLevel(record.status),
    message: `${record.feature}.backend.${record.step}`,
    data: buildPayload(record),
  })
}

export function recordBackendError(
  record: Omit<BackendCallRecord, "response"> & { error: unknown }
) {
  const axiosLike = record.error as {
    response?: { status?: number; statusText?: string; data?: unknown }
    code?: string
    message?: string
  }

  recordBackendResponse({
    feature: record.feature,
    step: record.step,
    endpoint: record.endpoint,
    method: record.method,
    flow: record.flow,
    request: record.request,
    durationMs: record.durationMs,
    status: axiosLike.response?.status ?? record.status ?? 0,
    statusText: axiosLike.response?.statusText ?? record.statusText,
    response:
      axiosLike.response?.data ??
      scrubValue({
        message: axiosLike.message,
        code: axiosLike.code,
      }),
  })
}

export async function invokeBackendCall<T>(
  record: Omit<
    BackendCallRecord,
    "status" | "statusText" | "response" | "durationMs"
  >,
  call: () => Promise<T>
): Promise<T> {
  const started = performance.now()

  try {
    const response = await call()
    recordBackendResponse({
      ...record,
      status: 200,
      response,
      durationMs: Math.round(performance.now() - started),
    })
    return response
  } catch (error) {
    recordBackendError({
      ...record,
      error,
      durationMs: Math.round(performance.now() - started),
    })
    throw error
  }
}
